import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { PaginationInfo } from "../../types/api";

interface AssetContextType {
  assets: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchAssets: (page?: number, perPage?: number) => Promise<void>;
  addAsset: (assetDetails: any) => Promise<void>;
  editAsset: (assetDetails: any) => Promise<void>;
  deleteAsset: (id: number) => Promise<void>;
}

export const AssetContext = createContext<AssetContextType | undefined>(undefined);

interface AssetProviderProps {
  children: ReactNode;
}

const AssetProvider: React.FC<AssetProviderProps> = ({ children }) => {
  const [assets, setAssets] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAssets = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/assets?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let assetsData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          assetsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          assetsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          assetsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        assetsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        assetsData = responseData;
      }
      
      setAssets(assetsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch assets:", error);
      setAssets([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addAsset = async (assetDetails: any): Promise<void> => {
    try {
      const response = await api.post("/assets", assetDetails);
      const newAsset = response.data.data;
      setAssets((prevAssets) => [...prevAssets, newAsset]);
    } catch (error) {
      console.error("Failed to add asset:", error);
      throw error;
    }
  };

  const editAsset = async (assetDetails: any): Promise<void> => {
    try {
      const response = await api.put(`/assets/${assetDetails.id}`, assetDetails);
      const updatedAsset = response.data.data;
      setAssets((prevAssets) =>
        prevAssets.map((item) =>
          item.id === updatedAsset.id ? updatedAsset : item
        )
      );
    } catch (error) {
      console.error("Failed to edit asset:", error);
      throw error;
    }
  };

  const deleteAsset = async (id: number): Promise<void> => {
    try {
      await api.delete(`/assets/${id}`);
      setAssets((prevAssets) => prevAssets.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete asset:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchAssets(1, 15);
  }, [fetchAssets]);

  return (
    <AssetContext.Provider
      value={{ assets, pagination, loading, fetchAssets, addAsset, editAsset, deleteAsset }}
    >
      {children}
    </AssetContext.Provider>
  );
};

export default AssetProvider;
